import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiPhone, FiMail, FiImage, FiList, FiStar, FiMapPin, FiCheckCircle,
  FiSmartphone, FiArrowRight, FiFacebook, FiGlobe, FiMessageCircle,
  FiCamera, FiShare2,
} = FiIcons;

const features = [
  { icon: FiPhone,      title: 'Click-to-Call Button',       body: 'One tap and the homeowner is calling you. No digging for a number.' },
  { icon: FiList,       title: 'Full Service List',           body: 'Everything you offer — clearly listed so visitors know exactly what to call you for.' },
  { icon: FiCamera,     title: 'Photo Gallery',               body: 'Showcase your best work. Before/afters, job site photos, finished projects.' },
  { icon: FiMail,       title: 'Contact Form',                body: 'Homeowners can submit a job request directly from the page, 24/7.' },
  { icon: FiStar,       title: 'Reviews Section',             body: 'Display your Google reviews right on the page to build instant credibility.' },
  { icon: FiMapPin,     title: 'Service Area Map',            body: 'Show exactly which cities and zip codes you cover so the right people contact you.' },
  { icon: FiShare2,     title: 'Social Media Links',          body: 'Facebook, Instagram — linked so visitors can follow or message you there too.' },
  { icon: FiSmartphone, title: 'Mobile-First Design',         body: 'Most homeowners scan QR codes on their phone. Your page looks great on every device.' },
];

export default function Websites() {
  return (
    <div className="pt-24 min-h-screen bg-brand-cream">

      {/* Hero */}
      <section className="bg-brand-dark py-24 px-4 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-blue rounded-full blur-[140px]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-brand-yellow/20 text-brand-yellow px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-6 border border-brand-yellow/30">
            Included With Your First Run
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-white uppercase tracking-tighter italic leading-none mb-6">
            Your Landing Page.<br />
            <span className="text-brand-blue">Ready Before Drop Day.</span>
          </h1>
          <p className="text-xl text-brand-cream/60 font-bold max-w-2xl mx-auto mb-10 leading-relaxed">
            When a homeowner scans the QR code on your mailer or your business card, they land on your Mailbox Digital page — a professional one-page site built for your trade, included free with your first run.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://www.facebook.com/thegyrohero"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-brand-blue text-white font-black uppercase text-lg px-10 py-5 rounded-[2rem] hover:bg-brand-blue/90 transition-all hover:scale-105 active:scale-95 shadow-2xl shadow-brand-blue/30"
            >
              <SafeIcon icon={FiFacebook} /> Message Sean to Get Started
            </a>
          </div>
        </div>
      </section>

      {/* How it connects */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-black text-brand-brown uppercase tracking-tighter italic">How It All Connects</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            {[
              { icon: FiMail,      label: 'Mailer Hits 5,000 Homes',    sub: 'Homeowner picks it up, reads your ad' },
              { icon: FiSmartphone,label: 'They Scan the QR Code',       sub: 'On the mailer or your business card' },
              { icon: FiGlobe,     label: 'They Land on Your Page',      sub: 'Call, form submit, or save your number' },
            ].map((item, i) => (
              <div key={i} className="relative flex flex-col items-center gap-4 bg-brand-cream border-2 border-brand-brown/10 rounded-[2rem] p-8">
                {i < 2 && (
                  <div className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                    <SafeIcon icon={FiArrowRight} className="text-brand-blue text-2xl" />
                  </div>
                )}
                <div className="w-14 h-14 bg-brand-blue/10 rounded-2xl flex items-center justify-center text-brand-blue text-2xl">
                  <SafeIcon icon={item.icon} />
                </div>
                <div>
                  <p className="font-black text-brand-brown uppercase tracking-tight">{item.label}</p>
                  <p className="text-brand-brown/50 font-bold text-sm mt-1">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* QR code demo */}
          <div className="mt-16 flex flex-col md:flex-row items-center justify-center gap-10 bg-brand-cream border-2 border-brand-brown/10 rounded-[2rem] p-10">
            <div className="flex flex-col items-center gap-3">
              <QRCodeSVG
                value="https://mailboxdigital.com"
                size={120}
                fgColor="#1E3A5F"
                bgColor="transparent"
                level="M"
              />
              <span className="text-xs font-black uppercase tracking-widest text-brand-brown/40">Demo QR Code</span>
            </div>
            <div className="max-w-sm text-center md:text-left">
              <h3 className="text-2xl font-black text-brand-brown uppercase tracking-tight mb-3">This QR Code Goes Everywhere</h3>
              <p className="text-brand-brown/60 font-bold leading-relaxed">
                It appears on your mailer ad, on the back mailing panel, and on your 250 business cards. Every scan goes directly to your Mailbox Digital landing page.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-4 bg-brand-dark">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-brand-blue/20 text-brand-blue text-xs font-black uppercase tracking-widest px-4 py-1.5 rounded-full mb-4 border border-brand-blue/30">What's On Your Page</span>
            <h2 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tighter italic leading-none">
              Everything a Homeowner<br />Needs to Call You
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                className="bg-white/5 border border-white/10 rounded-[2rem] p-7 hover:border-brand-blue/40 transition-all group"
              >
                <div className="w-12 h-12 bg-brand-blue/10 rounded-xl flex items-center justify-center text-brand-blue text-xl mb-5 group-hover:bg-brand-blue group-hover:text-white transition-all">
                  <SafeIcon icon={f.icon} />
                </div>
                <h3 className="font-black text-white uppercase tracking-tight mb-2">{f.title}</h3>
                <p className="text-brand-cream/50 font-bold text-sm leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What's included callout */}
      <section className="py-24 px-4 bg-brand-cream">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white border-4 border-brand-blue rounded-[2.5rem] p-10 md:p-14 shadow-[12px_12px_0px_#009FE3]">
            <div className="text-center mb-10">
              <span className="inline-block bg-brand-yellow text-brand-brown text-xs font-black uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Included Free — First Run Only</span>
              <h2 className="text-3xl md:text-5xl font-black text-brand-brown uppercase tracking-tighter italic leading-none">
                Your Full Package
              </h2>
            </div>
            <ul className="space-y-4 mb-10">
              {[
                '2.6″ × 3.75″ ad on the co-op mailer — 5,000 homes',
                '250 two-sided business cards · 16pt full color',
                'QR code printed on your ad and your cards',
                'Mailbox Digital landing page — built and live before your drop',
                'Click-to-call, contact form, photo gallery, service list',
                'Reviews section, service area, social links',
                'Category exclusivity — one trade per city per run',
                'Design help included for your ad and cards',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-4">
                  <SafeIcon icon={FiCheckCircle} className="text-brand-blue text-xl shrink-0 mt-0.5" />
                  <span className="font-bold text-brand-brown">{item}</span>
                </li>
              ))}
            </ul>
            <div className="text-center border-t-2 border-brand-brown/10 pt-8">
              <div className="text-brand-brown/40 font-bold uppercase tracking-widest text-xs mb-2">All of the above</div>
              <div className="text-6xl font-black text-brand-blue mb-2">$375</div>
              <div className="text-brand-brown/40 font-bold uppercase tracking-widest text-xs mb-8">per run · no contracts</div>
              <a
                href="https://www.facebook.com/thegyrohero"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-brand-blue text-white font-black uppercase text-lg px-12 py-5 rounded-[2rem] hover:bg-brand-dark transition-all hover:scale-105 active:scale-95 shadow-xl"
              >
                <SafeIcon icon={FiFacebook} /> Reserve Your Spot
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-brand-dark text-center">
        <div className="max-w-2xl mx-auto">
          <SafeIcon icon={FiMessageCircle} className="text-brand-blue text-5xl mx-auto mb-6" />
          <h2 className="text-4xl font-black text-white uppercase tracking-tighter italic mb-4">
            Ready to Get Your Page Live?
          </h2>
          <p className="text-brand-cream/50 font-bold mb-8">
            Message Sean on Facebook to check if your trade category is still open in your city.
          </p>
          <a
            href="https://www.facebook.com/thegyrohero"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-brand-yellow text-brand-brown font-black uppercase text-xl px-14 py-6 rounded-[2rem] hover:opacity-90 transition-all hover:scale-105 active:scale-95"
          >
            <SafeIcon icon={FiFacebook} className="text-brand-blue" /> Message Sean
          </a>
        </div>
      </section>

    </div>
  );
}
